import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Phone, Mail, MapPin, Clock, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    try {
      // In a real application, you would send this data to your backend
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Message envoyé !",
        description: "Nous vous recontacterons dans les plus brefs délais.",
      });
      
      // Reset form
      setFormData({
        name: "",
        phone: "",
        email: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneClick = () => {
    window.location.href = "tel:0658864706";
  };

  const handleEmailClick = () => {
    window.location.href = "mailto:js.concept@orange.fr";
  };

  const interventionZones = [
    "Gries et environs",
    "Strasbourg", 
    "Haguenau",
    "Saverne",
    "Molsheim",
    "Sélestat"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-primary-glow to-secondary py-20 lg:py-32">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-white">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              Contactez-nous
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              Contactez-nous
            </h1>
            <p className="text-xl mb-8 text-white/90 leading-relaxed max-w-3xl mx-auto">
              Une question ? Un projet ? N'hésitez pas à nous contacter. 
              Nous sommes là pour vous accompagner dans tous vos travaux de peinture et décoration.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Demande de devis ou d'information</CardTitle>
                  <p className="text-muted-foreground">
                    Remplissez ce formulaire et nous vous recontacterons rapidement pour discuter de votre projet.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nom complet *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder="Votre nom complet"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Téléphone *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          placeholder="06 XX XX XX XX"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="votre@email.com"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="subject">Sujet de votre demande</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="Ex: Devis peinture intérieure"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message">Votre message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        placeholder="Décrivez votre projet en détail..."
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-primary hover:bg-primary/90"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Envoi en cours..." : "Envoyer ma demande"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Contact Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl">Devis gratuit sous 48h</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center">
                      <Phone className="h-5 w-5 mr-2 text-primary" />
                      Téléphone
                    </h3>
                    <Button 
                      variant="outline" 
                      onClick={handlePhoneClick}
                      className="w-full justify-start"
                    >
                      06 58 86 47 06
                    </Button>
                    <p className="text-sm text-muted-foreground mt-2">
                      Appeler maintenant
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center">
                      <Mail className="h-5 w-5 mr-2 text-primary" />
                      Email
                    </h3>
                    <Button 
                      variant="outline" 
                      onClick={handleEmailClick}
                      className="w-full justify-start"
                    >
                      js.concept@orange.fr
                    </Button>
                    <p className="text-sm text-muted-foreground mt-2">
                      Réponse sous 24h
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center">
                      <MapPin className="h-5 w-5 mr-2 text-primary" />
                      Adresse
                    </h3>
                    <p className="text-sm">5 Rue des peupliers</p>
                    <p className="text-sm">67240 Gries</p>
                  </div>
                </CardContent>
              </Card>

              {/* Hours */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl flex items-center">
                    <Clock className="h-5 w-5 mr-2" />
                    Horaires d'ouverture
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Lundi - Vendredi</span>
                      <span className="font-semibold">8h00 - 18h00</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Samedi</span>
                      <span className="text-muted-foreground">Fermé</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Dimanche</span>
                      <span className="text-muted-foreground">Fermé</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Emergency */}
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center text-orange-800">
                    <AlertTriangle className="h-5 w-5 mr-2" />
                    Urgences
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-orange-700 mb-3">
                    Pour les urgences (dégâts des eaux, etc.), contactez-nous directement :
                  </p>
                  <Button 
                    onClick={handlePhoneClick}
                    className="w-full bg-orange-600 hover:bg-orange-700"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    06 58 86 47 06
                  </Button>
                </CardContent>
              </Card>

              {/* Intervention Zone */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl">Zone d'intervention</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Nous intervenons dans les secteurs suivants :
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {interventionZones.map((zone, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                        {zone}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    Nous nous déplaçons gratuitement pour étudier votre projet et vous proposer 
                    un devis détaillé et personnalisé.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Deplacements Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6">Déplacements</h2>
          <p className="text-lg text-muted-foreground mb-8">
            Devis gratuit à domicile
          </p>
          <p className="text-muted-foreground">
            Nous nous déplaçons gratuitement pour étudier votre projet et vous proposer 
            un devis détaillé et personnalisé.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;